/**
 * AK-423 (PR-E) — мини-карта контура поля для вкладки «Геометрия» карточки поля.
 *
 * Заменяет прежнюю голую SVG-панель (контур на белом фоне) настоящей MapLibre-
 * картой с подложкой (спутник Esri / схема OSM), поверх которой рисуются
 * эталонный контур и зоны как ЛОКАЛЬНЫЕ GeoJSON-слои. Никакого MVT — это
 * карточка поля, а не /map (векторные поля/scope-навигация там). Мини-карта
 * читает те же данные, что и список справа (referenceGeo из /contours, zones из
 * /subzones), и не трогает /map, LayerManager, registry.
 *
 * Режим «Изменить контур» переиспользует боевой terra-draw-редактор
 * ContourEditorController (AK-321/371, тот же, что «Обвести поле» на /map):
 * перетаскивание вершин, вставка вершины на ребре (midpoint), удаление вершины
 * (double-click), валидация самопересечений. Изоляция ADR-038: редактор рендерит
 * в свой namespace `td-edit-`, не задевая наши слои. Сохранение — через колбэк
 * onSaveGeometry (владелец мутации — вкладка), геометрия PUT'ится в существующий
 * /fields/{id}/geometry (бэкенд не трогаем).
 *
 * Файл в src/components/map/ — whitelist для hex-цветов (MapLibre paint требует
 * конкретные цвета, не CSS var()). Тема резолвится из CSS-переменных в рантайме
 * (тот же приём, что earthtech.resolveAmberHex).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Check,
  Circle,
  Combine,
  Eraser,
  Leaf,
  Loader2,
  Map as MapIcon,
  MousePointer2,
  Pencil,
  Redo2,
  Satellite,
  Scissors,
  Spline,
  Square,
  Undo2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { installMapResizeGuard } from '@/lib/map/installMapResizeGuard';
import { isMapAlive } from '@/lib/map/isMapAlive';
import { ContourEditorController } from '@/lib/map/editor/ContourEditorController';
import type { ShapeTool } from '@/lib/map/editor/editorStore';
import type { CircleMode } from '@/lib/map/measure/measureStore';
import { clearFieldGeometryCache } from '@/lib/map/editor/fieldGeojson';
import { resolveAmberHex } from '@/lib/map/editor/earthtech';
import type { EditorShapes } from '@/components/fields/card/zoneSplit';
import { loadFieldNdviDates } from './fieldNdviDates';
import { FieldNdviLegend } from '@/components/fields/FieldNdviLegend';
import { IMAGERY_TILE_URL, OSM_STREETS_PATH } from '@/lib/map/basemapUrls';
import {
  geoBounds,
  toFeatureCollection,
  zonesFeatureCollection,
  emptyFC,
  type MiniGeo,
} from './fieldContourGeojson';

// ── подложки (те же источники, что и на /map basemap) ─────────────────────
// Перф-аудит 14.08 (корень 1): спутник — через наш кэширующий прокси.
const SATELLITE_URL = IMAGERY_TILE_URL();
const STREETS_URL = OSM_STREETS_PATH;

// Эталонный контур — белая обводка + едва заметная белая заливка (читается на
// спутнике). Зоны красятся фирменными/chart-цветами (data-driven, ['get','color']).
const REF_HEX = '#ffffff';

type Basemap = 'satellite' | 'streets' | 'ndvi';

// AK-442: контрастный NDVI как подложка редактора — по нему видны круги
// поливов. Тайлы scope-фильтруются farm_id (AK-153), cookie same-origin.
const NDVI_SRC = 'ndvi-underlay';
const NDVI_LAYER = 'ndvi-underlay';

function ndviTilesUrl(date: string, farmId: string): string {
  return `${window.location.origin}/api/v1/ndvi/raster/ndvi-contrast/${date}/{z}/{x}/{y}.png?farm_id=${farmId}`;
}

/** AK-460: инструменты рисования поливных площадей (тот же движок, что /map). */
const ZONE_TOOLS: ReadonlyArray<{
  key: ShapeTool;
  label: string;
  hint: string;
  icon: typeof Square;
}> = [
  { key: 'circle', label: 'Круг', hint: 'Тяните от центра к краю', icon: Circle },
  { key: 'rectangle', label: 'Прямоуг.', hint: 'Тяните угол в угол', icon: Square },
  { key: 'polygon', label: 'Полигон', hint: 'Клики по точкам, замкните', icon: Spline },
];

const MONTHS_RU = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];

/** Зона для отрисовки: геометрия + вид/индекс (цвет резолвится внутри). */
export interface MiniZone {
  id: string;
  geojson: MiniGeo | null;
  kind: string;
  index: number;
}

interface Props {
  fieldId: string;
  /** farm_id поля — обязателен для NDVI-подложки (scope-фильтр тайлов). */
  farmId?: string | null;
  referenceGeo: MiniGeo | null;
  zones: MiniZone[];
  hidden: Set<string>;
  /** Показывать ли эталонный контур (глаз в списке справа). */
  referenceHidden: boolean;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  /** Сохранить геометрию (владелец мутации — вкладка). Бросает при ошибке. */
  onSaveGeometry: (geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: unknown;
  }) => Promise<void>;

  // ── AK-460: режим «Добавить поливные площади» ПРЯМО НА ЭТОЙ карте ────────
  /**
   * Инлайн-режим рисования поливных площадей (был отдельной модалкой —
   * founder: «зачем отдельно окно с попапом, надо было на том же экране»).
   * Контур поля и подложки (в т.ч. NDVI) остаются активны — круги рисуются
   * поверх снимка.
   */
  zonesEditing?: boolean;
  /** Набор нарисованных фигур наверх (вкладка считает из них зоны). */
  onZoneShapesChange?: (shapes: EditorShapes) => void;
  /** Пресет «Полив-круг + углы»: кольцо, которым наполнить редактор. */
  zonesSeed?: { ring: number[][]; signal: number } | null;
  /** Монотонный счётчик «начать с чистого листа». */
  zonesResetSignal?: number;

  // ── AK-515: правка границ СУЩЕСТВУЮЩЕЙ зоны ──────────────────────────────
  /**
   * Зона, границу которой правим тем же редактором, что и контур поля.
   * `ring` — внешнее кольцо её нынешней границы (владелец — вкладка, она же
   * решает, какие зоны редактируемы). null = режим выключен.
   */
  zoneEdit?: { id: string; name: string; ring: number[][] } | null;
  /** Сохранить новую границу зоны (мутация — на вкладке). Бросает при ошибке. */
  onSaveZoneGeometry?: (geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: unknown;
  }) => Promise<void>;
  /** Выйти из режима правки зоны (Отмена / успешное сохранение). */
  onZoneEditCancel?: () => void;
}

// ── резолв темы в hex (MapLibre paint не принимает CSS var()) ──────────────
function cssHex(varName: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const probe = document.createElement('span');
  probe.style.cssText = `position:absolute;left:-9999px;top:-9999px;color:hsl(var(${varName}))`;
  document.body.appendChild(probe);
  try {
    const m = getComputedStyle(probe).color.match(/(\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return fallback;
    const h = (n: number) => n.toString(16).padStart(2, '0');
    return `#${h(+m[1])}${h(+m[2])}${h(+m[3])}`;
  } catch {
    return fallback;
  } finally {
    probe.remove();
  }
}

interface ZonePalette {
  irrigated_pivot: string;
  dryland_corner: string;
  cycle: string[];
}

function buildZonePalette(): ZonePalette {
  return {
    irrigated_pivot: cssHex('--primary', '#2f7d3a'),
    dryland_corner: resolveAmberHex(),
    cycle: [
      cssHex('--chart-3', '#4c9f70'),
      cssHex('--chart-4', '#d98c3f'),
      cssHex('--chart-5', '#8a6fb0'),
      cssHex('--chart-1', '#3f7d3a'),
      cssHex('--chart-2', '#c9a227'),
    ],
  };
}

/**
 * Набор фигур из геометрии редактора: Polygon = одна фигура (кольца =
 * внешнее + дырки), MultiPolygon = по фигуре на часть.
 */
function toShapes(
  geometry:
    | { type: 'Polygon'; coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] }
    | null,
): EditorShapes {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return [geometry.coordinates];
  return geometry.coordinates;
}

function zoneHex(pal: ZonePalette, kind: string, index: number): string {
  if (kind === 'irrigated_pivot') return pal.irrigated_pivot;
  if (kind === 'dryland_corner') return pal.dryland_corner;
  return pal.cycle[index % pal.cycle.length];
}

export function FieldContourMiniMap({
  fieldId,
  farmId,
  referenceGeo,
  zones,
  hidden,
  referenceHidden,
  editing,
  onEditingChange,
  onSaveGeometry,
  zonesEditing = false,
  onZoneShapesChange,
  zonesSeed,
  zonesResetSignal = 0,
  zoneEdit = null,
  onSaveZoneGeometry,
  onZoneEditCancel,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const editorRef = useRef<ContourEditorController | null>(null);
  const paletteRef = useRef<ZonePalette | null>(null);
  const [ready, setReady] = useState(false);
  const [basemap, setBasemap] = useState<Basemap>('satellite');

  // ── AK-442: даты NDVI (контрастный) для подложки ─────────────────────────
  const [ndviDates, setNdviDates] = useState<string[] | null>(null);
  const [ndviDate, setNdviDate] = useState<string | null>(null);
  const [ndviMonth, setNdviMonth] = useState<number | null>(null);
  const [ndviLoading, setNdviLoading] = useState(false);

  // ── состояние режима правки ──────────────────────────────────────────────
  const [editArea, setEditArea] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);

  // ── AK-460: состояние режима «Добавить поливные площади» ─────────────────
  const [zoneTool, setZoneTool] = useState<ShapeTool | null>(null);
  const [zoneCircleMode, setZoneCircleMode] = useState<CircleMode>('center');
  const [zoneShapeCount, setZoneShapeCount] = useState(0);
  const [zoneCanUndo, setZoneCanUndo] = useState(false);
  const [zoneCanRedo, setZoneCanRedo] = useState(false);
  const [zoneCanCut, setZoneCanCut] = useState(false);
  const [zoneCutMode, setZoneCutMode] = useState(false);
  const [zoneError, setZoneError] = useState<string | null>(null);
  /** Инструмент, который перевзводим после каждой нарисованной фигуры. */
  const stickyToolRef = useRef<ShapeTool | null>(null);
  /** Свежий колбэк набора фигур (не пересоздаёт редактор при ре-рендере). */
  const zoneShapesCb = useRef(onZoneShapesChange);
  zoneShapesCb.current = onZoneShapesChange;

  // AK-515: правка границ зоны. Кольцо держим в ref — иначе новая ссылка на
  // массив при каждом рендере вкладки пересоздавала бы редактор (и стирала
  // уже подвинутые вершины). Эффект зависит только от id зоны.
  const zoneEditRef = useRef(zoneEdit);
  zoneEditRef.current = zoneEdit;
  const zoneEditId = zoneEdit?.id ?? null;

  // ── создание карты (один раз) ────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    paletteRef.current = buildZonePalette();

    const map = new maplibregl.Map({
      container: containerRef.current,
      attributionControl: { compact: true },
      style: {
        version: 8,
        sources: {
          sat: {
            type: 'raster',
            tiles: [SATELLITE_URL],
            tileSize: 256,
            attribution: 'Esri, Maxar, Earthstar Geographics',
          },
          streets: {
            type: 'raster',
            tiles: [STREETS_URL],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors',
          },
          contour: { type: 'geojson', data: emptyFC() },
          zones: { type: 'geojson', data: emptyFC() },
        },
        layers: [
          { id: 'sat', type: 'raster', source: 'sat', layout: { visibility: 'visible' } },
          { id: 'streets', type: 'raster', source: 'streets', layout: { visibility: 'none' } },
          {
            id: 'zones-fill',
            type: 'fill',
            source: 'zones',
            paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.35 },
          },
          {
            id: 'zones-line',
            type: 'line',
            source: 'zones',
            paint: { 'line-color': ['get', 'color'], 'line-width': 2 },
          },
          {
            id: 'contour-fill',
            type: 'fill',
            source: 'contour',
            paint: { 'fill-color': REF_HEX, 'fill-opacity': 0.1 },
          },
          {
            id: 'contour-line',
            type: 'line',
            source: 'contour',
            paint: { 'line-color': REF_HEX, 'line-width': 2 },
          },
        ],
      },
      center: [55, 55],
      zoom: 3,
    });
    mapRef.current = map;

    const detachResize = installMapResizeGuard(map, containerRef.current);
    map.once('load', () => setReady(true));

    return () => {
      detachResize();
      // Сначала снять редактор (его terra-draw teardown трогает слои карты),
      // потом убрать карту (isMapAlive-гварды внутри).
      editorRef.current?.deactivate();
      editorRef.current = null;
      try {
        map.remove();
      } catch {
        // карта уже снята гонкой размонтирования — игнор.
      }
      mapRef.current = null;
    };
  }, []);

  // ── отрисовка контура + зон (вне режима правки) ──────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !isMapAlive(map) || editing) return;
    const pal = paletteRef.current ?? buildZonePalette();

    const contourSrc = map.getSource('contour') as maplibregl.GeoJSONSource | undefined;
    contourSrc?.setData(
      referenceHidden ? emptyFC() : toFeatureCollection(referenceGeo, { id: 'reference' }),
    );

    const zonesSrc = map.getSource('zones') as maplibregl.GeoJSONSource | undefined;
    // Правим границу зоны — её статичную копию убираем, иначе под редактором
    // остаётся «призрак» старой границы и не видно, что именно ты подвинул.
    // Соседние зоны, наоборот, нужны: по ним видно, куда границу двигать нельзя.
    const off = zoneEditId ? new Set([...hidden, zoneEditId]) : hidden;
    zonesSrc?.setData(
      zonesFeatureCollection(
        zones.map((z) => ({
          id: z.id,
          geojson: z.geojson,
          color: zoneHex(pal, z.kind, z.index),
        })),
        off,
      ),
    );
  }, [ready, editing, referenceGeo, zones, hidden, referenceHidden, zoneEditId]);

  // ── fitBounds по контуру/зонам ───────────────────────────────────────────
  const fitToContent = useCallback(() => {
    const map = mapRef.current;
    if (!map || !isMapAlive(map)) return;
    const b = geoBounds([referenceGeo, ...zones.map((z) => z.geojson)]);
    if (!b) return;
    try {
      map.fitBounds(b, { padding: 28, duration: 0, maxZoom: 17 });
    } catch {
      // карта не готова к камере — игнор.
    }
  }, [referenceGeo, zones]);

  useEffect(() => {
    if (ready && !editing) fitToContent();
  }, [ready, editing, fitToContent]);

  // ── переключение подложки ────────────────────────────────────────────────
  // NDVI = спутник снизу + контрастный NDVI поверх (тайлы покрывают только
  // поля хозяйства, остальное — спутниковый контекст).
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !isMapAlive(map)) return;
    try {
      map.setLayoutProperty('sat', 'visibility', basemap !== 'streets' ? 'visible' : 'none');
      map.setLayoutProperty('streets', 'visibility', basemap === 'streets' ? 'visible' : 'none');
    } catch {
      // слой ещё не готов — визибилити применится на следующем ready.
    }
  }, [ready, basemap]);

  // ── AK-460: даты NDVI берём ПО ПОЛЮ (лениво, при включении подложки) ─────
  // Было (AK-442): 5 чанков /satellite/dates по ФЕРМЕ — роут требует
  // data.scene_complete + data.field_indices_v2, которых для исторических
  // сцен может не быть → «Нет снимков за год» при живых снимках. См.
  // fieldNdviDates.ts — там разбор и порядок источников.
  // «Идёт запрос» держим в ref, а НЕ в зависимостях эффекта. С `ndviLoading`
  // в deps выходил дедлок: setNdviLoading(true) перезапускал эффект, cleanup
  // абортил свой же запрос, а повторный заход выходил по `ndviLoading` — и
  // полоска навсегда оставалась в «Даты NDVI…». Поймано браузерным прогоном.
  const ndviReqRef = useRef(false);
  useEffect(() => {
    if (basemap !== 'ndvi' || !fieldId || ndviDates !== null || ndviReqRef.current) {
      return;
    }
    const ac = new AbortController();
    ndviReqRef.current = true;
    setNdviLoading(true);
    void loadFieldNdviDates(fieldId, ac.signal)
      .then((sorted) => {
        if (ac.signal.aborted) return;
        setNdviDates(sorted);
        const last = sorted[sorted.length - 1] ?? null;
        setNdviDate(last);
        setNdviMonth(last ? Number(last.slice(5, 7)) - 1 : null);
      })
      .finally(() => {
        ndviReqRef.current = false;
        setNdviLoading(false);
      });
    return () => ac.abort();
  }, [basemap, fieldId, ndviDates]);

  // ── AK-442: слой NDVI на карте (re-create на смену даты — без кэш-грабель) ─
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !isMapAlive(map)) return;
    const removeNdvi = () => {
      try {
        if (map.getLayer(NDVI_LAYER)) map.removeLayer(NDVI_LAYER);
        if (map.getSource(NDVI_SRC)) map.removeSource(NDVI_SRC);
      } catch {
        // карта размонтируется — игнор.
      }
    };
    if (basemap !== 'ndvi' || !farmId || !ndviDate) {
      removeNdvi();
      return;
    }
    removeNdvi();
    try {
      map.addSource(NDVI_SRC, {
        type: 'raster',
        tiles: [ndviTilesUrl(ndviDate, farmId)],
        tileSize: 256,
        minzoom: 9,
        maxzoom: 16,
      });
      // Под зонами/контуром: NDVI — подложка, разметка должна читаться поверх.
      map.addLayer(
        { id: NDVI_LAYER, type: 'raster', source: NDVI_SRC, paint: { 'raster-opacity': 1.0 } },
        'zones-fill',
      );
    } catch {
      // стиль ещё грузится — эффект перезапустится по ready.
    }
    return removeNdvi;
  }, [ready, basemap, farmId, ndviDate]);

  // ── редактор: ОДИН на три режима — контур, новые площади, границы зоны ────
  // AK-460 (founder): тулбокс рисования один и тот же во всех режимах —
  // круг/прямоугольник/полигон, вырезать, объединить, очистить, undo/redo.
  // Разница только в источнике фигур: контур подгружается из поля (traceField),
  // поливные площади рисуются с нуля поверх контура, а граница существующей
  // зоны (AK-515) приезжает готовым кольцом из вкладки.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !isMapAlive(map)) return;
    if (!editing && !zonesEditing && !zoneEditId) return;
    // Правка контура — старший режим: пока она идёт, зонные режимы молчат.
    const zoneGeomMode = Boolean(zoneEditId) && !editing;
    const zonesMode = zonesEditing && !editing && !zoneGeomMode;

    setEditArea(null);
    setEditError(null);
    setZoneError(null);
    if (!zonesMode && !zoneGeomMode) {
      // Скрыть статический контур — редактор рисует свой amber-полигон поверх.
      // В режиме зон контур, наоборот, НУЖЕН: это граница обрезки.
      try {
        map.setLayoutProperty('contour-fill', 'visibility', 'none');
        map.setLayoutProperty('contour-line', 'visibility', 'none');
      } catch {
        // слой мог не успеть — не критично.
      }
    }

    const ctrl = new ContourEditorController(map, {
      onResult: (r) => setEditArea(r?.primary ?? null),
      onHasFeature: () => {},
      onSnapping: () => {},
      onLoading: () => {},
      onError: (e) => (zonesMode ? setZoneError(e) : setEditError(e)),
      onHistoryChange: ({ canUndo, canRedo }) => {
        setZoneCanUndo(canUndo);
        setZoneCanRedo(canRedo);
      },
      onGeometryChange: (geometry) => {
        if (zonesMode) zoneShapesCb.current?.(toShapes(geometry));
      },
      onShapeState: (s) => {
        setZoneShapeCount(s.shapeCount);
        setZoneCanCut(s.canCut);
        setZoneCutMode(s.cutMode);
        setZoneTool(s.activeTool);
        // AK-508 (founder UX-решение): БЕЗ авто-перевзвода инструмента после
        // каждой фигуры. Коммит фигуры (mouseup) сам заканчивает режим
        // «Рисовать» (activeTool → null) — landing-состояние теперь режим
        // «Выбрать»: нарисовал → сразу можно подвинуть/поправить именно ЕЁ.
        // Следующую фигуру того же типа — явный повторный клик по кнопке
        // инструмента или по сегменту «Рисовать» (см. ниже), без скрытого
        // авто-рисования, которое на поле «фигура на весь экран» приводило к
        // тому, что рисование не стартовало вовсе (AK-508, прод-блокер).
      },
    });
    editorRef.current = ctrl;
    ctrl.activate();

    if (zoneGeomMode) {
      // Правка границы зоны начинается с её нынешних вершин — как «Изменить
      // контур», только геометрия уже на руках (запроса за ней не надо).
      const ring = zoneEditRef.current?.ring;
      if (ring?.length) ctrl.loadRing(ring as [number, number][]);
      else setEditError('У зоны нет пригодной границы для правки.');
    } else if (zonesMode) {
      // Нулевой порог входа (founder: «не смог ничего нарисовать») — круг уже
      // выбран, можно сразу тянуть по снимку.
      stickyToolRef.current = 'circle';
      ctrl.setCircleMode('center');
      ctrl.beginShape('circle');
      setZoneTool('circle');
    } else {
      // Правка контура начинается с вершин существующей геометрии.
      void ctrl.traceField(fieldId);
    }

    return () => {
      stickyToolRef.current = null;
      ctrl.deactivate();
      editorRef.current = null;
      setZoneShapeCount(0);
      setZoneCanUndo(false);
      setZoneCanRedo(false);
      setZoneCanCut(false);
      setZoneCutMode(false);
      setZoneTool(null);
      if (zonesMode) zoneShapesCb.current?.([]);
      if (!zonesMode && !zoneGeomMode && isMapAlive(map)) {
        try {
          map.setLayoutProperty('contour-fill', 'visibility', 'visible');
          map.setLayoutProperty('contour-line', 'visibility', 'visible');
        } catch {
          // карта размонтируется — игнор.
        }
      }
    };
  }, [ready, editing, zonesEditing, zoneEditId, fieldId]);

  const pickZoneTool = useCallback((tool: ShapeTool) => {
    setZoneError(null);
    const ctrl = editorRef.current;
    if (!ctrl) return;
    // Повторный клик по активному инструменту — снять его (иначе «липкий»
    // инструмент держит dragPan выключенным и карта не таскается).
    if (stickyToolRef.current === tool) {
      stickyToolRef.current = null;
      ctrl.cancelTool();
      setZoneTool(null);
      return;
    }
    stickyToolRef.current = tool;
    ctrl.beginShape(tool);
  }, []);

  // Пресет «Полив-круг + углы» наполняет редактор вписанным кругом.
  useEffect(() => {
    const ctrl = editorRef.current;
    if (!zonesEditing || !ctrl || !zonesSeed?.ring?.length) return;
    ctrl.loadRing(zonesSeed.ring as [number, number][]);
  }, [zonesEditing, zonesSeed?.signal, zonesSeed?.ring]);

  // «Нарисовать вручную» / «Очистить» — чистый лист.
  useEffect(() => {
    if (!zonesResetSignal || !zonesEditing) return;
    setZoneError(null);
    editorRef.current?.clear();
    if (stickyToolRef.current) editorRef.current?.beginShape(stickyToolRef.current);
  }, [zonesResetSignal, zonesEditing]);

  const pickCircleMode = useCallback((mode: CircleMode) => {
    setZoneCircleMode(mode);
    editorRef.current?.setCircleMode(mode);
  }, []);

  /**
   * AK-515: правим границу зоны, а не контур поля (панель одна на оба).
   * Обработчик сохранения — часть условия: без него режим бессмысленен, зато
   * ниже вызов идёт напрямую, без «на всякий случай» optional-chaining.
   */
  const zoneGeomEditing = Boolean(zoneEditId) && !editing && onSaveZoneGeometry != null;

  async function handleSave() {
    const ctrl = editorRef.current;
    if (!ctrl) return;
    const geom = ctrl.currentGeometry();
    if (!geom) {
      setEditError(
        zoneGeomEditing
          ? 'Граница зоны пуста — нарисуйте её заново.'
          : 'Контур пуст — нарисуйте границу поля.',
      );
      return;
    }
    if (!geom.ok) {
      setEditError(geom.error);
      return;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      if (zoneGeomEditing) {
        await onSaveZoneGeometry(geom.geometry);
        onZoneEditCancel?.();
      } else {
        await onSaveGeometry(geom.geometry);
        // Геометрия поля изменилась — сбросить кэш /geojson, чтобы повторная
        // правка/обводка грузила свежий контур.
        clearFieldGeometryCache();
        onEditingChange(false);
      }
    } catch (e) {
      setEditError(
        e instanceof Error
          ? e.message
          : zoneGeomEditing
            ? 'Не удалось сохранить границы зоны.'
            : 'Не удалось сохранить контур.',
      );
    } finally {
      setEditBusy(false);
    }
  }

  return (
    <div className="absolute inset-0">
      {/* data-map-ready — чистый DOM-сигнал состояния `ready` (взводится
          ТОЛЬКО через `map.once('load', …)` выше), без побочного эффекта на
          вёрстку/поведение. Нужен e2e (см. ak508-draw-mode.spec.ts): раньше
          готовность карты приходилось угадывать таймаутом на CSS-класс
          инструмента, что маскировало целостный (структурный) WebGL-баг
          (AK-508 root cause 23.08) под обычный «медленный рендер». Явный
          флаг убирает гадание — тест ждёт ФАКТ готовности, а не
          фиксированное число миллисекунд.

          Зона head-web: атрибут добавлен в прод-компонент FieldContourMiniMap
          (не в тестовом харнессе) — чистый DOM-атрибут без влияния на
          вёрстку/поведение/DS, но owner компонента должен знать о новом
          e2e-хуке здесь. */}
      <div
        ref={containerRef}
        data-map-ready={ready ? 'true' : 'false'}
        className="h-full w-full"
      />

      {/* Тумблер подложки (слева снизу — не перекрывает подпись контура сверху). */}
      <div className="absolute bottom-2 left-2 flex overflow-hidden rounded-lg border border-border bg-card/90 text-xs shadow-sm backdrop-blur">
        <button
          type="button"
          onClick={() => setBasemap('satellite')}
          aria-pressed={basemap === 'satellite'}
          className={cn(
            'flex items-center gap-2 px-3 py-2 transition-colors',
            basemap === 'satellite'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          <Satellite className="h-3.5 w-3.5" />
          Спутник
        </button>
        <button
          type="button"
          onClick={() => setBasemap('streets')}
          aria-pressed={basemap === 'streets'}
          className={cn(
            'flex items-center gap-2 px-3 py-2 transition-colors',
            basemap === 'streets'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          <MapIcon className="h-3.5 w-3.5" />
          Схема
        </button>
        {farmId && (
          <button
            type="button"
            onClick={() => setBasemap('ndvi')}
            aria-pressed={basemap === 'ndvi'}
            title="Контрастный NDVI — по нему удобно рисовать круги поливов"
            className={cn(
              'flex items-center gap-2 px-3 py-2 transition-colors',
              basemap === 'ndvi'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <Leaf className="h-3.5 w-3.5" />
            NDVI
          </button>
        )}
      </div>

      {/* Легенда растра — та же `FieldNdviLegend`, что на «Обзоре» поля.
          До этого на «Геометрии» её не было вовсе: человек смотрел на цветной
          растр без ключа и должен был помнить палитру наизусть (design review
          2026-08-18, зона 1 — «две карты, два языка», быстрый шаг 2).
          Подложка здесь — контрастный NDVI (`ndvi-contrast`, см. `ndviTileUrl`
          выше), поэтому и легенда контрастная. */}
      {basemap === 'ndvi' && farmId && ndviDate && (
        <div className="pointer-events-none absolute right-2 top-2 z-10 max-w-[min(20rem,calc(100%-1rem))]">
          <FieldNdviLegend mode="ndvi_contrast" />
        </div>
      )}

      {/* AK-442: выбор месяца и даты NDVI-подложки. */}
      {basemap === 'ndvi' && farmId && (
        <div className="absolute bottom-2 left-1/2 flex max-w-[min(28rem,calc(100%-1rem))] -translate-x-1/2 items-center gap-2 rounded-lg border border-border bg-card/90 px-2 py-2 text-xs shadow-sm backdrop-blur">
          {ndviLoading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Даты NDVI…
            </span>
          ) : !ndviDates?.length ? (
            <span className="text-muted-foreground">Нет снимков за год</span>
          ) : (
            <>
              <select
                value={ndviMonth ?? ''}
                onChange={(e) => {
                  const m = Number(e.target.value);
                  setNdviMonth(m);
                  // Последняя дата выбранного месяца.
                  const inMonth = ndviDates.filter(
                    (d) => Number(d.slice(5, 7)) - 1 === m,
                  );
                  setNdviDate(inMonth[inMonth.length - 1] ?? null);
                }}
                aria-label="Месяц NDVI"
                className="rounded-md border border-border bg-card px-2 py-1"
              >
                {Array.from(new Set(ndviDates.map((d) => Number(d.slice(5, 7)) - 1))).map(
                  (m) => (
                    <option key={m} value={m}>
                      {MONTHS_RU[m]}
                    </option>
                  ),
                )}
              </select>
              <div className="flex max-w-[18rem] gap-1 overflow-x-auto">
                {ndviDates
                  .filter((d) => ndviMonth == null || Number(d.slice(5, 7)) - 1 === ndviMonth)
                  .map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setNdviDate(d)}
                      aria-pressed={ndviDate === d}
                      className={cn(
                        'shrink-0 rounded-md px-2 py-1 tabular-nums',
                        ndviDate === d
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {Number(d.slice(8, 10))}
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* AK-460/AK-515: ОДИН тулбокс на все режимы — правка контура, новые
          поливные площади и границы существующей зоны. Ширина текучая
          (min-w-0 + max-w), ряды переносятся — на 480px и в узком сплите
          ничего не режется. */}
      {(zonesEditing || editing || zoneGeomEditing) && (
        <div
          data-testid="zones-toolbox"
          className="absolute left-2 top-2 z-10 w-[13rem] min-w-0 max-w-[calc(100%-1rem)] space-y-2 overflow-x-hidden rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-lg"
        >
          {/* AK-508 (founder UX-решение, прямая цитата): «селектор —
              рисовать/таскать: когда рисовать — только рисуем, когда
              таскать — таскаем любую фигуру». Явный переключатель режима —
              не угадывание по координатам клика (это и было регрессией
              #603 на поле, где фигура покрывает весь вьюпорт). */}
          <div className="grid grid-cols-2 gap-1" data-testid="zones-mode-toggle">
            <Button
              variant={zoneTool ? 'default' : 'outline'}
              size="sm"
              className="h-7 min-w-0 px-2 text-xs"
              data-testid="zones-mode-draw"
              title="Рисовать — любой клик на карте строит новую фигуру"
              onClick={() => {
                if (zoneTool) return;
                setZoneError(null);
                const tool = stickyToolRef.current ?? 'circle';
                stickyToolRef.current = tool;
                editorRef.current?.beginShape(tool);
              }}
            >
              <Pencil size={14} />
              Рисовать
            </Button>
            <Button
              variant={!zoneTool ? 'default' : 'outline'}
              size="sm"
              className="h-7 min-w-0 px-2 text-xs"
              data-testid="zones-mode-select"
              title="Выбрать — клик по фигуре её выделяет (двигайте тело/вершины), клик мимо — панорама карты"
              onClick={() => {
                if (!zoneTool) return;
                stickyToolRef.current = null;
                editorRef.current?.cancelTool();
              }}
            >
              <MousePointer2 size={14} />
              Выбрать
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {ZONE_TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <Button
                  key={t.key}
                  variant={zoneTool === t.key ? 'default' : 'outline'}
                  size="sm"
                  className="h-auto flex-col gap-1 py-2"
                  data-testid={`zones-tool-${t.key}`}
                  title={t.hint}
                  onClick={() => pickZoneTool(t.key)}
                >
                  <Icon size={16} />
                  <span className="text-xs leading-none">{t.label}</span>
                </Button>
              );
            })}
          </div>

          {zoneTool === 'circle' && (
            <div className="flex items-center gap-1">
              <Button
                variant={zoneCircleMode === 'center' ? 'default' : 'outline'}
                size="sm"
                className="h-7 flex-1 text-xs"
                onClick={() => pickCircleMode('center')}
              >
                От центра
              </Button>
              <Button
                variant={zoneCircleMode === 'diameter' ? 'default' : 'outline'}
                size="sm"
                className="h-7 flex-1 text-xs"
                onClick={() => pickCircleMode('diameter')}
              >
                От края
              </Button>
            </div>
          )}

          {/* Булевы над фигурами — как в редакторе на /map. */}
          <div className="flex flex-wrap gap-1">
            <Button
              variant={zoneCutMode ? 'default' : 'outline'}
              size="sm"
              className="h-7 min-w-0 flex-1 basis-20 px-2 text-xs"
              disabled={!zoneCanCut && !zoneCutMode}
              data-testid="zones-cut-shape"
              title="Вырезать одну фигуру из другой — кликните по фигуре"
              onClick={() => {
                stickyToolRef.current = null;
                editorRef.current?.toggleCut();
              }}
            >
              <Scissors size={14} />
              Вырезать
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 min-w-0 flex-1 basis-20 px-2 text-xs"
              disabled={zoneShapeCount < 2}
              data-testid="zones-union"
              title="Объединить фигуры в одну"
              onClick={() => {
                stickyToolRef.current = null;
                editorRef.current?.unionShapes();
              }}
            >
              <Combine size={14} />
              Объединить
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 min-w-0 flex-1 basis-20 px-2 text-xs"
              disabled={zoneShapeCount === 0}
              data-testid="zones-clear"
              title={
                zonesEditing
                  ? 'Убрать все нарисованные площади'
                  : zoneGeomEditing
                    ? 'Убрать границу зоны и нарисовать заново'
                    : 'Удалить контур целиком'
              }
              onClick={() => {
                setZoneError(null);
                editorRef.current?.clear();
                if (stickyToolRef.current) {
                  editorRef.current?.beginShape(stickyToolRef.current);
                }
              }}
            >
              <Eraser size={14} />
              Очистить
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label="Отменить"
              title="Отменить (Ctrl+Z)"
              disabled={!zoneCanUndo}
              onClick={() => editorRef.current?.undo()}
            >
              <Undo2 size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label="Вернуть"
              title="Вернуть (Ctrl+Shift+Z)"
              disabled={!zoneCanRedo}
              onClick={() => editorRef.current?.redo()}
            >
              <Redo2 size={16} />
            </Button>
          </div>

          <p
            className={cn(
              'text-xs leading-snug',
              zoneError ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {zoneError ??
              (zoneTool
                ? 'Тяните по снимку от центра круга к краю.'
                : zoneShapeCount === 0
                  ? 'Выберите инструмент («Круг» уже под рукой) и тяните по снимку.'
                  : 'Режим «Выбрать»: кликните по фигуре — подвиньте её или вершину. «Рисовать» — новая фигура.')}
          </p>
        </div>
      )}

      {/* Панель режима правки — площадь на лету + Сохранить/Отмена.
          AK-515: та же панель обслуживает правку границ зоны. */}
      {(editing || zoneGeomEditing) && (
        /* AK-460: ширина текучая (w-52 + min-w-0 + max-w), по горизонтали не
           скроллим — фиксированный ряд кнопок раньше не влезал и «Отмена»
           обрезалась (founder UAT). */
        <div
          data-testid="contour-edit-panel"
          className="absolute right-2 top-2 z-10 max-h-[calc(100%-1rem)] w-52 min-w-0 max-w-[calc(100%-1rem)] space-y-2 overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-card/95 p-3 shadow-md backdrop-blur"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Pencil className="h-4 w-4 shrink-0 text-accent-brand" />
            {zoneGeomEditing ? 'Границы зоны' : 'Изменение контура'}
          </div>
          <p className="text-xs text-muted-foreground">
            {zoneGeomEditing
              ? `Правим границы зоны «${zoneEdit?.name ?? 'без названия'}». Контур поля и соседние зоны видны для ориентира. Вершины двигаются, точка на ребре добавляет, двойной клик удаляет.`
              : 'Инструменты слева — круг, прямоугольник, полигон, вырезание и объединение. Вершины нарисованного двигаются, точка на ребре добавляет, двойной клик удаляет.'}
          </p>
          <div className="rounded-lg bg-muted px-3 py-2 text-sm">
            <span className="text-muted-foreground">Площадь: </span>
            <span className="font-medium text-foreground">{editArea ?? '—'}</span>
          </div>
          {editError && (
            <p className="text-xs text-destructive">{editError}</p>
          )}
          {/* flex-wrap + basis: при нехватке ширины кнопки встают в столбик,
              а не режутся. Обе с min-w-0, чтобы текст сжимался, а не пух. */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="min-w-0 flex-1 basis-24"
              data-testid="contour-edit-save"
              onClick={handleSave}
              disabled={editBusy}
            >
              {editBusy ? (
                <Loader2 className="mr-1 h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <Check className="mr-1 h-4 w-4 shrink-0" />
              )}
              Сохранить
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="min-w-0 flex-1 basis-24"
              onClick={() =>
                zoneGeomEditing ? onZoneEditCancel?.() : onEditingChange(false)
              }
              disabled={editBusy}
            >
              <X className="mr-1 h-4 w-4 shrink-0" />
              Отмена
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FieldContourMiniMap;
